/**
 * Retry Manager
 * 
 * Handles retry logic with exponential backoff for network requests.
 * Provides configurable retry strategies for different types of failures.
 */

export interface RetryOptions {
  maxRetries?: number;
  baseDelay?: number; // Base delay in milliseconds
  maxDelay?: number; // Maximum delay in milliseconds
  backoffFactor?: number; // Multiplier for exponential backoff
  retryCondition?: (error: any, attempt: number) => boolean;
  onRetry?: (error: any, attempt: number, delay: number) => void;
}

export interface RetryResult<T> {
  data?: T;
  error?: any;
  attempts: number;
  totalDelay: number;
}

export class RetryManager {
  private static defaultOptions: Required<RetryOptions> = {
    maxRetries: 3,
    baseDelay: 1000,
    maxDelay: 30000,
    backoffFactor: 2,
    retryCondition: (error: any) => {
      // Retry on network errors and 5xx server errors
      if (!error.response) return true; // Network error
      const status = error.response.status;
      return status >= 500 || status === 408 || status === 429;
    },
    onRetry: () => {},
  };

  /**
   * Execute a function with retry logic
   */
  static async execute<T>(
    fn: () => Promise<T>,
    options: RetryOptions = {}
  ): Promise<RetryResult<T>> {
    const opts = { ...this.defaultOptions, ...options };
    let lastError: any;
    let totalDelay = 0;

    for (let attempt = 0; attempt <= opts.maxRetries; attempt++) {
      try {
        const data = await fn();
        return {
          data,
          attempts: attempt + 1,
          totalDelay,
        };
      } catch (error) {
        lastError = error;

        // Check if we should retry
        if (attempt === opts.maxRetries || !opts.retryCondition(error, attempt)) {
          break;
        }

        // Calculate delay with exponential backoff
        const delay = Math.min(
          opts.baseDelay * Math.pow(opts.backoffFactor, attempt),
          opts.maxDelay
        );

        // Add jitter to prevent thundering herd
        const jitter = delay * 0.1 * Math.random();
        const finalDelay = delay + jitter;

        totalDelay += finalDelay;

        // Call retry callback
        opts.onRetry(error, attempt + 1, finalDelay);

        // Wait before retry
        await this.delay(finalDelay);
      }
    }

    return {
      error: lastError,
      attempts: opts.maxRetries + 1,
      totalDelay,
    };
  }

  /**
   * Create a retryable fetch function
   */
  static async fetchWithRetry(
    url: string,
    options?: RequestInit & { retryOptions?: RetryOptions }
  ): Promise<Response> {
    const { retryOptions, ...fetchOptions } = options || {};

    const result = await this.execute(
      () => fetch(url, fetchOptions),
      retryOptions
    );

    if (result.error) {
      throw result.error;
    }

    return result.data as Response;
  }

  /**
   * Retry with circuit breaker pattern
   */
  static createCircuitBreaker<T>(
    fn: () => Promise<T>,
    options: {
      threshold?: number; // Failures before opening
      timeout?: number; // Time to try again when open
      retryOptions?: RetryOptions;
    } = {}
  ) {
    const { threshold = 5, timeout = 60000, retryOptions } = options;
    let failures = 0;
    let lastFailureTime = 0;
    let state: 'closed' | 'open' | 'half-open' = 'closed';

    return async (): Promise<T> => {
      const now = Date.now();

      // Check if circuit should reset
      if (state === 'open' && now - lastFailureTime > timeout) {
        state = 'half-open';
        failures = 0;
      }

      // Fail fast if circuit is open
      if (state === 'open') {
        throw new Error('Circuit breaker is open');
      }

      try {
        const result = await this.execute(fn, retryOptions);
        
        // Success - reset circuit if it was failing
        if (state !== 'closed') {
          state = 'closed';
          failures = 0;
        }

        return result.data as T;
      } catch (error) {
        failures++;
        lastFailureTime = now;

        // Open circuit if threshold reached
        if (failures >= threshold) {
          state = 'open';
        }

        throw error;
      }
    };
  }

  /**
   * Create a retry queue for offline scenarios
   */
  static createRetryQueue<T>() {
    const queue: Array<{
      fn: () => Promise<T>;
      resolve: (value: T) => void;
      reject: (error: any) => void;
      options?: RetryOptions;
    }> = [];

    let isProcessing = false;
    let isOnline = navigator.onLine;

    // Listen for online/offline events
    const handleOnline = () => {
      isOnline = true;
      processQueue();
    };

    const handleOffline = () => {
      isOnline = false;
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    const processQueue = async () => {
      if (isProcessing || !isOnline || queue.length === 0) {
        return;
      }

      isProcessing = true;

      while (queue.length > 0 && isOnline) {
        const item = queue.shift();
        if (!item) break;

        try {
          const result = await this.execute(item.fn, item.options);
          if (result.data) {
            item.resolve(result.data);
          } else if (result.error) {
            item.reject(result.error);
          }
        } catch (error) {
          item.reject(error);
        }
      }

      isProcessing = false;
    };

    return {
      add: (
        fn: () => Promise<T>,
        options?: RetryOptions
      ): Promise<T> => {
        return new Promise((resolve, reject) => {
          queue.push({ fn, resolve, reject, options });
          
          if (isOnline) {
            processQueue();
          }
        });
      },
      clear: () => {
        queue.length = 0;
      },
      size: () => queue.length,
      destroy: () => {
        window.removeEventListener('online', handleOnline);
        window.removeEventListener('offline', handleOffline);
        queue.length = 0;
      },
    };
  }

  /**
   * Simple delay helper
   */
  private static delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Create a specialized retry strategy for cache operations
   */
  static createCacheRetryOptions(): RetryOptions {
    return {
      maxRetries: 2,
      baseDelay: 500,
      maxDelay: 5000,
      backoffFactor: 1.5,
      retryCondition: (error: any) => {
        // Retry on network errors but not on 404s
        if (!error.response) return true;
        const status = error.response.status;
        return status >= 500 || status === 408;
      },
      onRetry: (error, attempt, delay) => {
        console.warn(`Cache retry attempt ${attempt} after ${delay}ms:`, error.message);
      },
    };
  }

  /**
   * Create a retry strategy for critical player data
   */
  static createCriticalDataRetryOptions(): RetryOptions {
    return {
      maxRetries: 5,
      baseDelay: 200,
      maxDelay: 10000,
      backoffFactor: 2,
      retryCondition: (error: any) => {
        // Aggressive retry for critical data
        if (!error.response) return true;
        const status = error.response.status;
        return status >= 400; // Retry on all client errors too
      },
      onRetry: (error, attempt, delay) => {
        console.error(`Critical data retry ${attempt}/${5} after ${delay}ms:`, error);
      },
    };
  }
}

export default RetryManager;
